import { HTTPError } from 'ky';
import type { AreaWorkspaceErrors } from 'types/area-workspace';
import { format0 } from 'utils/number';

// ky's `api` carries a prefixUrl, which rejects a leading slash — and every href the
// backend hands out has one.
export const apiPath = (href: string): string => href.replace(/^\//, '');

// Carries the reasons a workspace write was refused, so the caller shows the backend's
// own wording rather than ky's generic HTTP text.
export class WorkspaceRefused extends Error {
  readonly reasons: string[];

  constructor(reasons: string[]) {
    super('Workspace write refused');
    this.reasons = reasons;
  }
}

// Turns a failed workspace write into its reasons. A refusal's `details.incomplete`
// beats its generic message: the gate is one live map placement per area, so "Area is
// already on the map" arrives there while the message alone says only "not promotable".
export const toWorkspaceError = async (error: unknown): Promise<Error> => {
  if (!(error instanceof HTTPError)) {
    return error instanceof Error ? error : new Error('Workspace write failed');
  }

  const body = (await error.response
    .json()
    .catch(() => null)) as AreaWorkspaceErrors | null;
  const refused = body?.errors?.[0];
  const blockers = refused?.details?.incomplete;

  return new WorkspaceRefused(
    blockers?.length
      ? blockers
      : [refused?.message].filter((reason): reason is string => !!reason)
  );
};

// What to say about a refused workspace write. The backend's own wording where it gave
// one; otherwise a line that adds something, since the caller has already said which
// action failed — a 500 answers with an HTML page and no reasons at all, and repeating
// "could not create the workspace" twice tells nobody anything.
export const readWriteFailure = (
  error: unknown,
  fallback: string
): string | null => {
  if (!error) {
    return null;
  }
  if (error instanceof WorkspaceRefused && error.reasons.length > 0) {
    return error.reasons.join(' ');
  }
  return fallback;
};

/** The subtitle for a refusal that came with no reason of its own. */
export const UNEXPLAINED_FAILURE =
  'The server did not say why. Try again, and check the logs if it keeps failing.';

/** Wells inside a boundary before and after a geometry save. */
export interface WellCounts {
  before: number;
  after: number;
}

/**
 * The geometry toast's wells line: the count now, and the count before when it
 * moved.
 *
 * @param {WellCounts} counts - Wells inside the boundary before and after.
 * @return {string} The sentence.
 */
export const wellsSentence = ({ before, after }: WellCounts): string => {
  const wells = `${format0(after)} ${after === 1 ? 'well' : 'wells'}`;
  const verb = after === 1 ? 'intersects' : 'intersect';
  return before === after
    ? `${wells} ${verb} the boundary.`
    : `${wells} now ${verb} the boundary (was ${format0(before)}).`;
};
